// Top-right toolbar — the app's entire nav chrome now that the sidebar is
// gone. Holds the view-lens picker, a Search (⌘K) affordance, contextual
// page links (the pages you're *not* currently on), and the signed-in user
// with sign-out. Timeline is home; content renders full-width beneath.
import { signOut } from "firebase/auth";
import { LogOut } from "lucide-react";
import { Link, useLocation } from "wouter";
import { auth } from "@/auth/firebase";
import { useEffectiveRole } from "@/auth/role";
import { BrandMark } from "@/components/BrandMark";
import { useUser } from "@/auth/useUser";
import { Glossary } from "@/components/Glossary";
import { PersonaSelector } from "@/components/PersonaSelector";

/** Detects macOS so the ⌘K hint matches the actual hotkey. */
const isMac =
  typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

export function TopBar({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { user } = useUser();
  const { isEditor } = useEffectiveRole();
  const [location] = useLocation();

  // Primary destinations, shown next to the brand. The brand lockup itself is
  // the home link, so Timeline isn't repeated here — only the pages you can
  // go TO and aren't already on. Analytics is editor-only.
  const pages = isEditor
    ? [
        {
          href: "/analytics",
          label: "Analytics",
          active: location.startsWith("/analytics"),
        },
      ]
    : [];
  const links = pages.filter((p) => !p.active);

  const divider = <span aria-hidden className="h-4 w-px bg-border" />;

  return (
    <header className="fixed inset-x-0 top-0 z-30 border-b border-border bg-bg/90 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-2.5 text-[12.5px] text-text-secondary sm:gap-2.5 sm:px-6">
        {/* ── Left: identity + primary nav ───────────────────────────── */}
        <Link
          href="/timeline"
          className="flex items-center gap-2 no-underline hover:border-b-transparent"
          title="Release Hub — home"
        >
          <BrandMark className="h-[18px] w-[18px] shrink-0" />
          {/* Wordmark drops below sm so the toolbar fits a phone in one row —
              the mark stays the home link and the Timeline masthead carries
              the name anyway. */}
          <span className="hidden font-serif text-[15px] leading-none text-text sm:inline">
            Release Hub
          </span>
        </Link>
        {links.length > 0 && divider}
        {links.map((l) => (
          <Link
            key={l.href}
            href={l.href}
            className="rounded px-1.5 py-1 no-underline text-text-secondary hover:text-text"
          >
            {l.label}
          </Link>
        ))}

        <span className="flex-1" />

        {/* ── Right: lens + tools ─────────────────────────────────────── */}
        <PersonaSelector />
        <button
          type="button"
          onClick={onOpenPalette}
          title="Search (features, releases, flags)"
          className="flex items-center gap-1.5 rounded-md border border-border-hover bg-card/60 px-2 py-1 text-text-tertiary hover:border-text/40 hover:text-text"
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          {/* The hotkey hint is meaningless on touch — hide it on mobile,
              keep the magnifier as a tappable search affordance. */}
          <kbd className="hidden rounded bg-border/60 px-1 py-0.5 text-[10px] text-text-tertiary sm:inline-block">
            {isMac ? "⌘K" : "Ctrl K"}
          </kbd>
        </button>

        {divider}

        {/* ── Right: reference / help ─────────────────────────────────── */}
        <Glossary />
        <a
          href="https://docs.google.com/document/d/1Uy048qBJgqVIVAWVf6bKeamrCm7MpLo2FgircNXHwow/edit?tab=t.0"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Reference Doc"
          className="inline-flex items-center gap-1 rounded-md border border-border-hover bg-card/60 px-2 py-1 text-text-tertiary no-underline hover:border-text/40 hover:text-text"
          title="Open the Release Hub guide (Google Doc)"
        >
          {/* Label collapses to the external-link glyph on mobile. */}
          <span className="hidden sm:inline">Reference Doc</span>
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="M6 3h7v7M13 3 6.5 9.5M11 9.5V13H3V5h3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>

        {divider}

        {/* ── Right: account ──────────────────────────────────────────── */}
        {/* The email is orientation, not an action — drop it on mobile and
            keep just the sign-out control. */}
        <span className="hidden max-w-[22ch] truncate sm:inline">
          {user?.email ?? user?.displayName ?? "signed in"}
        </span>
        <button
          aria-label="Sign out"
          className="inline-flex items-center rounded p-1 text-text-tertiary hover:bg-border hover:text-text"
          onClick={() => signOut(auth)}
        >
          {/* Icon-only on mobile to reclaim the row; labelled text on sm+. */}
          <LogOut className="h-4 w-4 sm:hidden" aria-hidden />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
    </header>
  );
}
